<template>
    <OrderBooksTable>
        <template #filters>
            <DropdownFilter
                :pin="pinFilters"
                @apply="applyFilters"
                @clear="clearFilters"
                @toggle-pin="togglePinFilters"
            >
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <Datepicker
                            v-model="filtersFields.created_at"
                            v-model:dateFrom="filtersFields.date_from"
                            v-model:dateTo="filtersFields.date_to"
                            placeholder="created_at"
                            name="filter_created_at"
                            datemode="range"
                            :disabled="disableFilter('created_at')"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.account_uid"
                            :options="accountSelectOptions"
                            name="account_uid"
                            class="w-full"
                            :disabled="disableFilter('account_uid')"
                            :placeholder="$t('account')"
                            :on-search="loadAccountSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem v-if="!initialFilters?.asset_uid">
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.asset_uid"
                            :options="assetSelectOptions"
                            name="asset_uid"
                            class="w-full"
                            :disabled="disableFilter('asset_uid')"
                            :placeholder="$t('asset')"
                            :on-search="loadAssetSelectOptions"
                        />
                    </label>
                </DropdownFilterItem>

                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.type"
                            name="type"
                            :options="mapEnumToArray(SmAssetType)"
                            :reduce="reduceEnumLabel(SmAssetType)"
                            :placeholder="$t('type')"
                            :disabled="disableFilter('type')"
                            @keydown.enter.prevent="handleEnterPress"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.direction"
                            name="direction"
                            :options="mapEnumToArray(SmOrderBookDirectionType)"
                            :reduce="reduceEnumLabel(SmOrderBookDirectionType)"
                            :placeholder="$t('direction')"
                            :disabled="disableFilter('direction')"
                            @keydown.enter.prevent="handleEnterPress"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <SelectField
                            v-model="filtersFields.source"
                            name="source"
                            :options="mapEnumToArray(SmOrderBookSourceType)"
                            :reduce="reduceEnumLabel(SmOrderBookSourceType)"
                            :placeholder="$t('source')"
                            :disabled="disableFilter('source')"
                            @keydown.enter.prevent="handleEnterPress"
                        />
                    </label>
                </DropdownFilterItem>
                <DropdownFilterItem>
                    <label class="flex items-center">
                        <TextField
                            v-model="filtersFields.quantity"
                            name="quantity"
                            :placeholder="$t('quantity')"
                            :disabled="disableFilter('quantity')"
                        />
                    </label>
                </DropdownFilterItem>
            </DropdownFilter>
        </template>
    </OrderBooksTable>
</template>

<script setup lang="ts">
import { useEnum, useTable } from '@composables/common'
import {
    Datepicker,
    DropdownFilter,
    DropdownFilterItem,
    SelectField,
    TextField,
} from '@src/components'
import {
    Asset,
    CreateOrderPayload,
    Platform,
    TableSorting,
    OrderBook,
    Account,
} from '@src/types'
import { getAsset, getAssets } from '@src/utils/api/assets'
import { getOrderBook } from '@src/utils/api/secondaryMarket'
import { getPlatforms } from '@src/utils/api/platforms'
import { getAccounts } from '@utils/api/accounts'
import { handleEnterPress } from '@src/utils/helpers'
import { formatSecondaryMarketValue } from '@partials/secondaryMarket/formatSecondaryMarketValue'
import moment from 'moment'
import { onMounted, ref, toRefs } from 'vue'
import {
    SmAssetType,
    SmOrderBookDirectionType,
    SmOrderBookSourceType,
} from '@enums/SecondaryMarket'

const assetSelectOptions = ref<{ label: string; value: string | null }[]>([])
const accountSelectOptions = ref<
    { label: string | null; value: string | null }[]
>([])

const props = withDefaults(
    defineProps<{
        initialFilters?: {
            id?: string | null
            asset_uid?: string | null
            created_at?: string | null
            platform_name_exact?: string | null
            account_title?: string | null
            type?: string | null
            source?: string | null
            direction?: string | null
            quantity?: number | null
            account_uid?: string | null
        } | null
        createInitialValues?: CreateOrderPayload | null
        createDisabledFields?: string[] | null
    }>(),
    {
        initialFilters: null,
        createInitialValues: null,
        createDisabledFields: null,
    }
)

const { initialFilters } = toRefs(props)

const { mapEnumToArray, reduceEnumLabel, getEnumLabel } = useEnum()

const orderBooks = ref<OrderBook[]>([])
const orderBooksCount = ref<number>(0)
const platforms = ref<Platform[]>([])

const disableFilter = (filter: string) => {
    if (initialFilters.value) {
        return Object.keys(initialFilters.value).includes(filter)
    }
    return false
}

const {
    TableWrapper: OrderBooksTable,
    limit,
    sorting,
    page,
    filtersFields,
    filters,
    setFilters,
    pinFilters,
    togglePinFilters,
    applyFilters,
    clearFilters,
} = useTable({
    loadData: loadOrderBook,
    filters: {
        asset_uid: null,
        created_at: null,
        date_from: null,
        date_to: null,
        platform_name_exact: null,
        account_title: null,
        type: null,
        source: null,
        direction: null,
        quantity: null,
    },
    disableFilters: initialFilters.value
        ? Object.keys(initialFilters.value)
        : [],
    tableProps: {
        id: 'order-books-table',
        label: 'order_books_table',
        help: 'help_order_books_table',
        pagination: true,
        data: orderBooks,
        total: orderBooksCount,
        sorting: ref<TableSorting[]>([
            {
                field: 'created_at',
                direction: 'desc',
            },
        ]),
        columns: [
            {
                label: 'created_at',
                key: 'created_at',
                sorting: true,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return props.rowData.created_at
                        ? moment(props.rowData.created_at).format('DD.MM.YYYY')
                        : '-'
                },
            },
            {
                label: 'account',
                key: 'account_title',
                url: accountUrl,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return (
                        props.rowData.account?.account_title ??
                        props.rowData.account_uid
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'asset',
                key: 'asset_title',
                url: assetUrl,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return (
                        props.rowData.asset?.asset_title ??
                        props.rowData.asset_uid
                    )
                },
                sorting: true,
                copy: true,
            },
            {
                label: 'type',
                key: 'type',
                sorting: true,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return getEnumLabel(SmAssetType, props.rowData.type)
                },
            },
            {
                label: 'direction',
                key: 'direction',
                sorting: true,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return getEnumLabel(
                        SmOrderBookDirectionType,
                        props.rowData.direction
                    )
                },
            },
            {
                label: 'source',
                key: 'source',
                sorting: true,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return getEnumLabel(
                        SmOrderBookSourceType,
                        props.rowData.source
                    )
                },
            },
            {
                label: 'quantity',
                key: 'quantity',
                sorting: true,
                cellRenderer: (props: { rowData: OrderBook }) => {
                    return formatSecondaryMarketValue(props.rowData.quantity)
                },
            },
        ],
    },
})

function accountUrl(row: OrderBook) {
    if (!row.account) return ''
    return row.account.legal_profile_id
        ? '/legalProfile/' + row.account.legal_profile_id
        : '/naturalProfile/' + row.account.natural_profile_id
}

function assetUrl(row: OrderBook) {
    const assetUid = row.asset?.asset_uid ?? row.asset_uid
    return '/assets/' + assetUid
}

async function loadOrderBook() {
    const response = await getOrderBook(
        filters.value,
        page.value,
        limit.value,
        sorting.value
    )

    if (response.status === 206 || response.status === 200) {
        orderBooks.value = response.data.data ?? response.data
        orderBooksCount.value =
            response.data.meta?.count ?? response.data.count ?? 0
    }

    const platformsRequest = await getPlatforms()
    platforms.value = platformsRequest.data.platforms
}

async function loadAssetSelectOptions(search: string) {
    const assetsRequest = await getAssets({
        'filter[asset_title]': search,
    })
    assetSelectOptions.value = assetsRequest.data.assets.map((asset: Asset) => {
        return {
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        }
    })
}

async function loadAccountSelectOptions(search: string) {
    const request = await getAccounts({
        'filter[account_title]': search,
        'filter[platform_name_exact]': props.initialFilters.platform_name_exact,
    })
    accountSelectOptions.value = request.data.accounts.map(
        (account: Account) => {
            return {
                label: account.account_title,
                value: account.account_uid,
            }
        }
    )
}

async function loadAssetSelectOptionsByUID(uid: string) {
    const assetRequest = await getAsset(uid)
    const asset = assetRequest.data.asset
    assetSelectOptions.value = [
        {
            label: asset.asset_title as string,
            value: asset.asset_uid as string,
        },
    ]
}

onMounted(async () => {
    if (initialFilters.value) {
        filtersFields.value = {
            ...filtersFields.value,
            ...initialFilters.value,
        }
        setFilters()
        await loadOrderBook()
    }

    if (filtersFields.value.asset_uid) {
        await loadAssetSelectOptionsByUID(
            filtersFields.value.asset_uid as string
        )
    }
})
</script>

<style lang="scss" scoped>
input {
    &:disabled {
        @apply disabled:bg-gray-100;
    }
}
</style>
